import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import { format, parseISO } from 'date-fns';

import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | GridComponentOption
    | DatasetComponentOption
    | LegendComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
    BarChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface VisitorLoginSplitData {
    period: string;
    period_start: string;
    total_visitors: number;
    logged_in_visitors: number;
    not_logged_in_visitors: number;
}

interface Props {
    data?: VisitorLoginSplitData[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const formatPeriodLabel = (date: Date, period: Props['period']) => {
    if (period === 'daily') {
        return format(date, 'MMM dd');
    }
    if (period === 'weekly') {
        return format(date, 'MMM dd');
    }
    if (period === 'monthly') {
        return format(date, 'MMM yyyy');
    }
    return format(date, 'QQQ yyyy');
};

const formatTooltipDate = (date: Date, period: Props['period']) => {
    if (period === 'daily') {
        return format(date, 'MMM dd, yyyy');
    }
    if (period === 'weekly') {
        return format(date, 'MMM dd, yyyy');
    }
    if (period === 'monthly') {
        return format(date, 'MMMM yyyy');
    }
    return format(date, 'QQQ yyyy');
};

const VisitorLoginSplitChart: React.FC<Props> = ({ data, period }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({
                    series: [
                        { data: [] },
                        { data: [] }
                    ]
                });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const loggedInData: number[] = [];
            const browsedOnlyData: number[] = [];
            const totalsByIndex: number[] = [];

            data.forEach(item => {
                let date: Date;
                try {
                    date = item.period_start ? parseISO(item.period_start) : new Date();
                    if (isNaN(date.getTime())) {
                        date = new Date(item.period_start);
                    }
                } catch (e) {
                    console.warn('Error parsing date:', item.period_start, e);
                    date = new Date(item.period_start || new Date());
                }

                const loggedIn = Number(item.logged_in_visitors) || 0;
                const browsedOnly = Number(item.not_logged_in_visitors) || 0;
                const total = Number(item.total_visitors) || loggedIn + browsedOnly;

                categories.push(formatPeriodLabel(date, period));
                loggedInData.push(loggedIn);
                browsedOnlyData.push(browsedOnly);
                totalsByIndex.push(total);
            });

            const formatBarLabel = (params: any) => {
                const total = totalsByIndex[params.dataIndex] || 0;
                const value = Number(params.value) || 0;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${value.toLocaleString()}\n${percentage}%`;
            };

            const option: ECOption = {
                legend: {
                    data: ['Logged In', 'Browsed Without Login'],
                    top: 0,
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                    formatter: (params: any) => {
                        if (!Array.isArray(params)) {
                            return '';
                        }

                        const dateParam = params[0];
                        const source = data[dateParam.dataIndex];
                        const date = new Date(source?.period_start || '');
                        const total = totalsByIndex[dateParam.dataIndex] || 0;
                        const lines = [`${formatTooltipDate(date, period)}`];

                        params.forEach((param: any) => {
                            const value = Number(param.value) || 0;
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                            lines.push(`${param.marker} ${param.seriesName}: ${value.toLocaleString()} (${percentage}%)`);
                        });

                        lines.push(`Total Visitors: ${total.toLocaleString()}`);
                        return lines.join('<br/>');
                    },
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '8%',
                    top: '18%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: {
                        rotate: categories.length > 10 ? 45 : 0,
                    },
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: (value: number) => `${value}`,
                    },
                },
                series: [
                    {
                        name: 'Logged In',
                        type: 'bar',
                        data: loggedInData,
                        barMaxWidth: 36,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                        itemStyle: {
                            color: '#52c41a',
                        },
                    },
                    {
                        name: 'Browsed Without Login',
                        type: 'bar',
                        data: browsedOnlyData,
                        barMaxWidth: 36,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                        itemStyle: {
                            color: '#1890ff',
                        },
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default VisitorLoginSplitChart;
